import {
  HubConnectionBuilder,
  HubConnectionState,
  type HubConnection,
} from '@microsoft/signalr';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/useAuth';

export interface GenerationEvent {
  jobId: string;
  status: 'queued' | 'processing' | 'complete' | 'failed' | string;
  payload?: unknown;
}

/**
 * Subscribes to /hubs/generation-status for a specific jobId. Returns the latest event
 * and a boolean indicating whether the subscription is live.
 */
export function useGenerationStatus(jobId: string | null | undefined) {
  const { getToken } = useAuth();
  const [event, setEvent] = useState<GenerationEvent | null>(null);
  const [connected, setConnected] = useState(false);
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;

    (async () => {
      const baseUrl =
        import.meta.env.VITE_SIGNALR_HUB_URL ?? 'http://localhost:5001/hubs';
      const url = `${baseUrl.replace(/\/$/, '')}/generation-status`;
      const token = (await getToken()) ?? '';

      const connection = new HubConnectionBuilder()
        .withUrl(url, { accessTokenFactory: () => token })
        .withAutomaticReconnect()
        .build();

      connection.on('status', (payload: GenerationEvent) => {
        if (!cancelled) setEvent(payload);
      });

      try {
        await connection.start();
        if (cancelled) {
          await connection.stop();
          return;
        }
        await connection.invoke('Subscribe', jobId);
        connectionRef.current = connection;
        setConnected(true);
      } catch (err) {
        console.warn('SignalR connection failed', err);
      }
    })();

    return () => {
      cancelled = true;
      const conn = connectionRef.current;
      if (conn && conn.state === HubConnectionState.Connected) {
        conn.invoke('Unsubscribe', jobId).catch(() => {});
        conn.stop().catch(() => {});
      }
      connectionRef.current = null;
      setConnected(false);
    };
  }, [jobId, getToken]);

  return { event, connected };
}
